// Odoo major-version upgrade wizard (admin). One panel that only ever shows the
// next action for the upgrade's current state: precheck → rehearsal on a clone
// → approve → production run → observation → commit or roll back.
// docs/superpowers/specs/2026-10-01-odoo-major-upgrade-design.md

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  upgradeApi,
  type Instance,
  type InstanceUpgrade,
  type UpgradeEngine,
  type UpgradeStatus,
} from '../../api/client'
import { isUpgradeBusy, useUpgradeAction, useUpgradePrecheck, useUpgradeStatus } from '../../hooks/useUpgrade'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  Field,
  Input,
  Select,
  Skeleton,
  Spinner,
} from '../../components/ui'
import { extractApiError } from './shared'

export function UpgradePanel({ instance }: { instance: Instance }) {
  const { t } = useTranslation()
  const status = useUpgradeStatus(instance.id)

  if (status.isLoading) return <Skeleton className="h-32" />
  if (status.isError || !status.data) {
    return <Alert>{extractApiError(status.error) ?? t('detail.upgrade.loadError')}</Alert>
  }
  const u = status.data.upgrade
  const open = u && !['committed', 'rolled_back', 'failed', 'cancelled'].includes(u.state)

  return (
    <div className="space-y-4">
      {u && !open && <LastUpgrade upgrade={u} />}
      {open ? <OpenUpgrade instance={instance} status={status.data} upgrade={u} /> : <StartUpgrade instance={instance} />}
      {u?.logs && (
        <details className="text-xs">
          <summary className="cursor-pointer text-gray-600">{t('detail.upgrade.logs')}</summary>
          <pre className="mt-2 max-h-96 overflow-auto rounded-lg bg-ink-900 p-3 text-ink-100 whitespace-pre-wrap">{u.logs}</pre>
        </details>
      )}
    </div>
  )
}

function LastUpgrade({ upgrade }: { upgrade: InstanceUpgrade }) {
  const { t } = useTranslation()
  const tone = upgrade.state === 'committed' ? 'success' : upgrade.state === 'cancelled' ? 'neutral' : 'danger'
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
      <span>{t('detail.upgrade.last', { from: upgrade.from_series, to: upgrade.to_series })}</span>
      <Badge tone={tone}>{t(`detail.upgrade.state.${upgrade.state}`)}</Badge>
      {upgrade.error && <span className="text-xs text-red-600">{upgrade.error}</span>}
    </div>
  )
}

// ─── Before an upgrade ───────────────────────────────────────────────────────

function StartUpgrade({ instance }: { instance: Instance }) {
  const { t } = useTranslation()
  const pre = useUpgradePrecheck(instance.id, true)
  const [engine, setEngine] = useState<UpgradeEngine | ''>('')
  const [error, setError] = useState('')
  const start = useUpgradeAction(instance.id, (e: UpgradeEngine) => upgradeApi.start(instance.id, e))

  if (pre.isLoading) return <Skeleton className="h-24" />
  if (pre.isError || !pre.data) {
    return <Alert>{extractApiError(pre.error) ?? t('detail.upgrade.loadError')}</Alert>
  }
  const p = pre.data
  const chosen = engine || p.engines[0] || ''

  return (
    <Card>
      <CardHeader
        title={t('detail.upgrade.title')}
        description={p.target_series
          ? t('detail.upgrade.fromTo', { from: p.from_series, to: p.target_series })
          : t('detail.upgrade.noTarget', { from: p.from_series })}
        className="mb-3"
      />
      {p.problems.length > 0 && (
        <Alert tone="warning" className="mb-3">
          <ul className="list-disc pl-4 space-y-1">
            {p.problems.map((code) => (
              <li key={code}>{t(`detail.upgrade.problem.${code}`, { to: p.target_series ?? '' })}</li>
            ))}
          </ul>
        </Alert>
      )}
      {p.ok && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">{t('detail.upgrade.intro')}</p>
          <Field label={t('detail.upgrade.engine')} hint={t(`detail.upgrade.engineHint.${chosen}`)}>
            {(props) => (
              <Select {...props} value={chosen} onChange={(e) => setEngine(e.target.value as UpgradeEngine)}>
                {p.engines.map((e) => (
                  <option key={e} value={e}>{t(`detail.upgrade.engineName.${e}`)}</option>
                ))}
              </Select>
            )}
          </Field>
          {error && <Alert>{error}</Alert>}
          <Button
            loading={start.isPending}
            onClick={() => {
              setError('')
              start.mutate(chosen as UpgradeEngine, {
                onError: (e) => setError(extractApiError(e) ?? t('detail.upgrade.actionError')),
              })
            }}
          >
            {t('detail.upgrade.startRehearsal')}
          </Button>
        </div>
      )}
    </Card>
  )
}

// ─── An open upgrade ─────────────────────────────────────────────────────────

function OpenUpgrade({ instance, status, upgrade: u }: { instance: Instance; status: UpgradeStatus; upgrade: InstanceUpgrade }) {
  const { t } = useTranslation()
  const busy = isUpgradeBusy(status)
  const [error, setError] = useState('')
  const [confirm, setConfirm] = useState<'cancel' | 'production' | 'commit' | 'rollback' | null>(null)

  const onError = (e: unknown) => setError(extractApiError(e) ?? t('detail.upgrade.actionError'))
  const cancel = useUpgradeAction(instance.id, () => upgradeApi.cancel(instance.id))
  const approve = useUpgradeAction(instance.id, () => upgradeApi.approve(instance.id))
  const production = useUpgradeAction(instance.id, () => upgradeApi.production(instance.id))
  const commit = useUpgradeAction(instance.id, () => upgradeApi.commit(instance.id))
  const rollback = useUpgradeAction(instance.id, () => upgradeApi.rollback(instance.id))

  const run = (m: typeof cancel) => {
    setError('')
    m.mutate(undefined, { onError, onSettled: () => setConfirm(null) })
  }
  const canCancel = !busy && (u.state === 'rehearsal' || u.state === 'rehearsal_approved'
    || (u.state === 'production' && u.stage === 'awaiting_dump'))

  return (
    <Card>
      <CardHeader
        title={t('detail.upgrade.fromTo', { from: u.from_series, to: u.to_series })}
        description={t(`detail.upgrade.engineName.${u.engine}`)}
        actions={<Badge tone={u.state === 'observing' ? 'accent' : 'warning'}>{t(`detail.upgrade.state.${u.state}`)}</Badge>}
        className="mb-3"
      />

      <div className="space-y-3">
        {busy && (
          <div className="flex items-center gap-2 text-sm text-gray-700" role="status">
            <Spinner />
            <span>{t(`detail.upgrade.stage.${u.stage}`)}</span>
          </div>
        )}
        {u.error && <Alert>{t('detail.upgrade.stageFailed', { stage: t(`detail.upgrade.stage.${u.stage}`) })}: {u.error}</Alert>}
        {error && <Alert>{error}</Alert>}

        {status.rehearsal && u.state !== 'observing' && (
          <p className="text-sm text-gray-600">
            {t('detail.upgrade.rehearsalAt')}{' '}
            <a href={status.rehearsal.url} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:text-brand-700">
              {status.rehearsal.subdomain}
            </a>{' '}
            <span className="text-xs text-gray-500">(Odoo {status.rehearsal.odoo_version})</span>
          </p>
        )}

        {u.state === 'rehearsal' && (u.stage === 'awaiting_code' || u.stage === 'migration_failed') && (
          <PrepareCode instance={instance} status={status} upgrade={u} onError={onError} />
        )}
        {u.stage === 'awaiting_dump' && <DumpUpload instance={instance} upgrade={u} onError={onError} />}

        {u.state === 'rehearsal' && u.stage === 'ready' && (
          <div className="space-y-2">
            <p className="text-sm text-gray-600">{t('detail.upgrade.readyHint')}</p>
            <Button loading={approve.isPending} onClick={() => run(approve)}>{t('detail.upgrade.approve')}</Button>
          </div>
        )}

        {u.state === 'rehearsal_approved' && (
          <div className="space-y-2">
            <p className="text-sm text-gray-600">{t('detail.upgrade.productionHint')}</p>
            <Button onClick={() => setConfirm('production')}>
              {u.error ? t('detail.upgrade.retryProduction') : t('detail.upgrade.startProduction')}
            </Button>
          </div>
        )}

        {u.state === 'observing' && (
          <Observation status={status} upgrade={u} onCommit={() => setConfirm('commit')} onRollback={() => setConfirm('rollback')} />
        )}

        {canCancel && (
          <Button variant="ghost" size="sm" onClick={() => setConfirm('cancel')}>{t('detail.upgrade.cancel')}</Button>
        )}
      </div>

      <ConfirmDialog
        open={confirm === 'cancel'}
        title={t('detail.upgrade.cancelTitle')}
        message={t('detail.upgrade.cancelMessage')}
        confirmLabel={t('detail.upgrade.cancel')}
        busy={cancel.isPending}
        onConfirm={() => run(cancel)}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'production'}
        tone="primary"
        title={t('detail.upgrade.productionTitle')}
        message={t('detail.upgrade.productionMessage', { to: u.to_series })}
        confirmLabel={t('detail.upgrade.startProduction')}
        busy={production.isPending}
        onConfirm={() => run(production)}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'commit'}
        tone="primary"
        title={t('detail.upgrade.commitTitle')}
        message={t('detail.upgrade.commitMessage', { from: u.from_series })}
        confirmLabel={t('detail.upgrade.commit')}
        busy={commit.isPending}
        onConfirm={() => run(commit)}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'rollback'}
        title={t('detail.upgrade.rollbackTitle')}
        message={t('detail.upgrade.rollbackMessage', {
          from: u.from_series,
          hours: hoursSince(u.cutover_at),
        })}
        confirmLabel={t('detail.upgrade.rollback')}
        busy={rollback.isPending}
        onConfirm={() => run(rollback)}
        onCancel={() => setConfirm(null)}
      />
    </Card>
  )
}

function hoursSince(iso?: string): number {
  if (!iso) return 0
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 3_600_000))
}

function PrepareCode({ instance, status, upgrade: u, onError }: {
  instance: Instance; status: UpgradeStatus; upgrade: InstanceUpgrade; onError: (e: unknown) => void
}) {
  const { t } = useTranslation()
  const addons = status.rehearsal?.addons ?? []
  const [refs, setRefs] = useState<Record<string, string>>({})
  const prepare = useUpgradeAction(instance.id, () => upgradeApi.prepareCode(instance.id, refs))
  const defaultRef = `${u.to_series}.0`

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">{t('detail.upgrade.codeHint', { to: u.to_series })}</p>
      {addons.length === 0 ? (
        <p className="text-sm text-gray-500">{t('detail.upgrade.noCustomAddons')}</p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
          {addons.map((a) => (
            <li key={a.name} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
              <span className="font-mono text-xs text-gray-800">{a.name} <span className="text-gray-500">{a.version}</span></span>
              {a.source_url ? (
                <Field className="w-40">
                  {(props) => (
                    <Input
                      {...props}
                      size="sm"
                      aria-label={t('detail.upgrade.refFor', { name: a.name })}
                      placeholder={defaultRef}
                      value={refs[a.name] ?? ''}
                      onChange={(e) => setRefs({ ...refs, [a.name]: e.target.value })}
                    />
                  )}
                </Field>
              ) : (
                <Badge tone="warning">{t('detail.upgrade.noSource')}</Badge>
              )}
            </li>
          ))}
        </ul>
      )}
      <Button loading={prepare.isPending} onClick={() => prepare.mutate(undefined, { onError })}>
        {u.engine === 'openupgrade' ? t('detail.upgrade.pullAndMigrate') : t('detail.upgrade.pullCode')}
      </Button>
    </div>
  )
}

function DumpUpload({ instance, upgrade: u, onError }: { instance: Instance; upgrade: InstanceUpgrade; onError: (e: unknown) => void }) {
  const { t } = useTranslation()
  const [file, setFile] = useState<File | null>(null)
  const upload = useUpgradeAction(instance.id, (f: File) => upgradeApi.uploadDump(instance.id, f))
  return (
    <div className="space-y-3">
      <Alert tone="accent">
        {u.state === 'production' ? t('detail.upgrade.dumpHintProduction') : t('detail.upgrade.dumpHintRehearsal')}
      </Alert>
      <Field label={t('detail.upgrade.dumpFile')}>
        {(props) => (
          <input
            {...props}
            type="file"
            accept=".dump"
            className="block text-sm"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        )}
      </Field>
      <Button disabled={!file} loading={upload.isPending} onClick={() => file && upload.mutate(file, { onError })}>
        {t('detail.upgrade.uploadDump')}
      </Button>
    </div>
  )
}

function Observation({ status, upgrade: u, onCommit, onRollback }: {
  status: UpgradeStatus; upgrade: InstanceUpgrade; onCommit: () => void; onRollback: () => void
}) {
  const { t } = useTranslation()
  const latest = status.observations[0]
  const red = status.verdict?.red ?? false
  const until = u.observe_until ? new Date(u.observe_until) : null
  const daysLeft = until ? Math.max(0, Math.ceil((until.getTime() - Date.now()) / 86_400_000)) : 0
  const cron = (n?: number) => (n === undefined || n < 0 ? '—' : String(n))

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge tone={latest ? (red ? 'danger' : 'success') : 'neutral'}>
          {latest ? (red ? t('detail.upgrade.verdictRed') : t('detail.upgrade.verdictGreen')) : t('detail.upgrade.verdictPending')}
        </Badge>
        {red && status.verdict?.reasons.map((r) => (
          <span key={r} className="text-xs text-red-700">{t(`detail.upgrade.reason.${r}`)}</span>
        ))}
        <span className="text-xs text-gray-500">{t('detail.upgrade.daysLeft', { count: daysLeft })}</span>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500">
            <th className="py-1 font-medium" />
            <th className="py-1 font-medium">{t('detail.upgrade.before', { v: u.from_series })}</th>
            <th className="py-1 font-medium">{t('detail.upgrade.now', { v: u.to_series })}</th>
          </tr>
        </thead>
        <tbody className="text-gray-800">
          <tr>
            <td className="py-1 text-gray-600">{t('detail.upgrade.errorsPerHour')}</td>
            <td>{u.baseline_errors_per_hour?.toFixed(1) ?? '—'}</td>
            <td>{latest ? latest.errors_per_hour.toFixed(1) : '—'}</td>
          </tr>
          <tr>
            <td className="py-1 text-gray-600">{t('detail.upgrade.failedCrons')}</td>
            <td>{cron(u.baseline_failed_crons)}</td>
            <td>{latest ? cron(latest.failed_crons) : '—'}</td>
          </tr>
          <tr>
            <td className="py-1 text-gray-600">{t('detail.upgrade.restarts')}</td>
            <td>—</td>
            <td>{latest ? latest.restart_count : '—'}</td>
          </tr>
        </tbody>
      </table>
      {red && <p className="text-xs text-gray-600">{t('detail.upgrade.redNoAutoCommit')}</p>}
      <div className="flex flex-wrap gap-2">
        <Button onClick={onCommit}>{t('detail.upgrade.commit')}</Button>
        <Button variant="danger" onClick={onRollback}>{t('detail.upgrade.rollback')}</Button>
      </div>
    </div>
  )
}
